import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  Modal,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import axios from 'axios';
import * as ImagePicker from 'expo-image-picker';
import { API_BASE } from '../utils';
import EnterpriseHeader from '../components/EnterpriseHeader';
import PadiProIcon from '../components/PadiProIcon';
import { PADIPRO, padiproShadow } from '../components/padiproTheme';

const WATER_OPTIONS = [
  { key: 'kering', label: 'Kering' },
  { key: 'normal', label: 'Normal' },
  { key: 'tinggi', label: 'Tinggi' },
];

const LEAF_OPTIONS = [
  { key: 'hijau', label: 'Hijau' },
  { key: 'kuning', label: 'Kuning' },
  { key: 'bercak', label: 'Bercak' },
  { key: 'layu', label: 'Layu' },
];

const WATER_META = {
  kering: { label: 'Kering', tone: '#a33a2a', bg: '#fff1ee', border: '#f6c6bd' },
  normal: { label: 'Normal', tone: '#1f6f43', bg: '#eaf8ef', border: '#bfe8cd' },
  tinggi: { label: 'Tinggi', tone: '#0f4f8f', bg: '#eaf3ff', border: '#c5dafb' },
};

const LEAF_META = {
  hijau: { label: 'Hijau', tone: '#1f6f43', bg: '#eaf8ef', border: '#bfe8cd' },
  kuning: { label: 'Kuning', tone: '#8b5e00', bg: '#fff8e5', border: '#f5df8c' },
  bercak: { label: 'Bercak', tone: '#9d5200', bg: '#fff1e6', border: '#f4c8a6' },
  layu: { label: 'Layu', tone: '#8a2440', bg: '#fff0f6', border: '#f3bdd4' },
};

const REPORT_PROGRESS_META = {
  waiting: {
    label: 'Menunggu Laporan',
    tone: '#0f4f8f',
    bg: '#eaf3ff',
    border: '#c5dafb',
    hint: 'Reminder sudah dikirim, menunggu form disubmit.',
  },
  ok: {
    label: 'OK',
    tone: '#1f6f43',
    bg: '#eaf8ef',
    border: '#bfe8cd',
    hint: 'Laporan sudah tersimpan di dashboard.',
  },
  unreminded: {
    label: 'Belum Diingatkan',
    tone: '#9a5c07',
    bg: '#fff8e8',
    border: '#f2ddab',
    hint: 'Reminder WA belum dikirim ke pekerja.',
  },
};

const ATTENTION_LEAF = new Set(['kuning', 'bercak', 'layu']);
const photoNeedsProxy = (value) => /^\/(uploads|api)\//i.test(String(value || '').trim());

const resolvePhotoUri = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (photoNeedsProxy(raw)) return `${API_BASE}${raw}`;
  if (raw.startsWith('/')) return `${API_BASE}${raw}`;
  return raw;
};

const getConditionSnapshot = (item) => {
  const water = item?.water_level || item?.last_water_level || null;
  const leaf = item?.leaf_condition || item?.last_leaf_condition || null;
  const notes = item?.notes || item?.last_notes || '';
  const photoPath = item?.photo_url || item?.photo_path || item?.last_photo_url || item?.last_photo_path || null;
  const checkedAt = item?.checked_at || item?.last_checked_at || item?.updated_at || item?.last_updated_at || null;
  const photoUri = resolvePhotoUri(photoPath);
  const needsAttention = water === 'kering' || ATTENTION_LEAF.has(leaf || '');
  return { water, leaf, notes, checkedAt, photoUri, needsAttention };
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toEpoch = (value) => {
  if (!value) return null;
  const n = new Date(value).getTime();
  return Number.isFinite(n) ? n : null;
};

const getReportProgress = (item) => {
  const serverProgress = String(item?.report_progress || '').trim().toLowerCase();
  if (serverProgress === 'waiting') return { key: 'waiting', ...REPORT_PROGRESS_META.waiting };
  if (serverProgress === 'unreminded') return { key: 'unreminded', ...REPORT_PROGRESS_META.unreminded };
  if (serverProgress === 'ok') return { key: 'ok', ...REPORT_PROGRESS_META.ok };

  if (String(item?.status || '').toLowerCase() === 'done') {
    return { key: 'ok', ...REPORT_PROGRESS_META.ok };
  }
  const remindedAt = toEpoch(item?.wa_last_notified_at);
  const checkedAt = toEpoch(item?.checked_at);
  if (remindedAt && (!checkedAt || checkedAt < remindedAt)) {
    return { key: 'waiting', ...REPORT_PROGRESS_META.waiting };
  }
  return { key: 'unreminded', ...REPORT_PROGRESS_META.unreminded };
};

const getErr = (e, fallback) =>
  e?.response?.data?.error || e?.message || fallback || 'Terjadi kesalahan';

const emptyChecklistState = {
  open: false,
  task: null,
  water: 'normal',
  leaf: 'hijau',
  notes: '',
  photo: null,
  previousPhotoUri: null,
  saving: false,
};

const emptyRemindState = {
  open: false,
  task: null,
  phone: '',
  sending: false,
};

const defaultSchedule = {
  enabled: false,
  time: '07:00',
  timezone: 'Asia/Jakarta',
  cadence_days: 5,
  dispatch_limit: 100,
};

const emptyAssignState = {
  open: false,
  task: null,
  selectedWorkerIds: [],
  phone: '',
  saving: false,
};

const normalizePhoneLocal = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('62')) return digits;
  if (digits.startsWith('0')) return `62${digits.slice(1)}`;
  if (digits.startsWith('8')) return `62${digits}`;
  return digits;
};

const parsePhoneListLocal = (value) => {
  const chunks = Array.isArray(value)
    ? value
    : String(value || '')
      .split(/[,\n;]+/g)
      .map((x) => String(x || '').trim())
      .filter(Boolean);
  const out = [];
  const seen = new Set();
  chunks.forEach((chunk) => {
    const phone = normalizePhoneLocal(chunk);
    if (!phone || seen.has(phone)) return;
    seen.add(phone);
    out.push(phone);
  });
  return out;
};

const formatPhoneListLocal = (arr) => parsePhoneListLocal(arr).join(', ');

const toYmdInput = (value) => {
  const raw = String(value || '').trim();
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const dt = new Date(raw);
  if (!Number.isNaN(dt.getTime())) return dt.toISOString().slice(0, 10);
  return new Date().toISOString().slice(0, 10);
};

export default function MonitoringScreen({ route }) {
  const token = route?.params?.token;
  const user = route?.params?.user;

  const authHdr = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const [tab, setTab] = useState('open');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [counts, setCounts] = useState({
    total: 0,
    pending: 0,
    overdue: 0,
    done: 0,
    waiting_report: 0,
    unreminded_report: 0,
  });
  const [cadence, setCadence] = useState(5);
  const [items, setItems] = useState([]);
  const [schedule, setSchedule] = useState(defaultSchedule);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [dispatchingNow, setDispatchingNow] = useState(false);
  const [historyExpandedByField, setHistoryExpandedByField] = useState({});
  const [workers, setWorkers] = useState([]);
  const [workersLoading, setWorkersLoading] = useState(false);

  const [checkModal, setCheckModal] = useState(emptyChecklistState);
  const [remindModal, setRemindModal] = useState(emptyRemindState);
  const [assignModal, setAssignModal] = useState(emptyAssignState);
  const [photoViewer, setPhotoViewer] = useState({
    open: false,
    uri: '',
    title: '',
  });

  const canRemind = user?.role === 'admin';
  const progressCounts = useMemo(() => {
    const acc = { waiting: 0, unreminded: 0, ok: 0 };
    (items || []).forEach((item) => {
      const p = getReportProgress(item);
      if (p?.key && Object.prototype.hasOwnProperty.call(acc, p.key)) acc[p.key] += 1;
    });
    return acc;
  }, [items]);
  const workerById = useMemo(() => {
    const map = new Map();
    (workers || []).forEach((w) => {
      const id = Number(w?.user_id);
      if (Number.isFinite(id)) map.set(id, w);
    });
    return map;
  }, [workers]);
  const workerIdByPhone = useMemo(() => {
    const map = new Map();
    (workers || []).forEach((w) => {
      const id = Number(w?.user_id);
      const phone = normalizePhoneLocal(w?.whatsapp_phone || '');
      if (Number.isFinite(id) && phone) map.set(phone, id);
    });
    return map;
  }, [workers]);

  const loadTasks = useCallback(
    async ({ silent = false, targetTab = tab } = {}) => {
      if (!token) return;
      if (!silent) setLoading(true);
      setErrorText('');
      try {
        const r = await axios.get(`${API_BASE}/monitoring/tasks`, {
          headers: authHdr,
          params: {
            status: targetTab,
            ensure: 1,
            limit: 300,
            cadence_days: 5,
          },
        });
        setItems(Array.isArray(r.data?.items) ? r.data.items : []);
        setCounts(r.data?.counts || {
          total: 0,
          pending: 0,
          overdue: 0,
          done: 0,
          waiting_report: 0,
          unreminded_report: 0,
        });
        setCadence(Number(r.data?.cadence_days || 5));
      } catch (e) {
        setErrorText(getErr(e, 'Gagal memuat daftar monitoring'));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [authHdr, tab, token]
  );

  useEffect(() => {
    loadTasks({ targetTab: tab });
  }, [loadTasks, tab]);

  useEffect(() => {
    if (!token) return undefined;
    const id = setInterval(() => {
      loadTasks({ silent: true, targetTab: tab });
    }, 20000);
    return () => clearInterval(id);
  }, [loadTasks, tab, token]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await loadTasks({ silent: true, targetTab: tab });
      if (canRemind) {
        const s = await axios.get(`${API_BASE}/monitoring/schedule`, { headers: authHdr });
        setSchedule((prev) => ({ ...prev, ...(s.data || {}) }));
        const w = await axios.get(`${API_BASE}/users/workers`, { headers: authHdr });
        setWorkers(Array.isArray(w.data?.items) ? w.data.items : []);
      }
    } finally {
      setRefreshing(false);
    }
  }, [authHdr, canRemind, loadTasks, tab]);

  const loadSchedule = useCallback(async () => {
    if (!canRemind) return;
    setScheduleLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/monitoring/schedule`, { headers: authHdr });
      setSchedule((prev) => ({ ...prev, ...(r.data || {}) }));
    } catch (e) {
      Alert.alert('Gagal load jadwal', getErr(e, 'Tidak bisa mengambil jadwal reminder'));
    } finally {
      setScheduleLoading(false);
    }
  }, [authHdr, canRemind]);

  const loadWorkers = useCallback(async ({ silent = false } = {}) => {
    if (!canRemind) return;
    if (!silent) setWorkersLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/users/workers`, { headers: authHdr });
      setWorkers(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch (e) {
      if (!silent) Alert.alert('Gagal load tukang', getErr(e, 'Tidak bisa mengambil daftar tukang kerja'));
    } finally {
      if (!silent) setWorkersLoading(false);
    }
  }, [authHdr, canRemind]);

  useEffect(() => {
    if (!canRemind) return;
    loadSchedule();
    loadWorkers({ silent: false });
  }, [canRemind, loadSchedule, loadWorkers]);

  const openChecklist = (task) => {
    const snapshot = getConditionSnapshot(task || {});
    setCheckModal({
      ...emptyChecklistState,
      open: true,
      task,
      water: task?.water_level || 'normal',
      leaf: task?.leaf_condition || 'hijau',
      notes: task?.notes || '',
      photo: null,
      previousPhotoUri: snapshot.photoUri || null,
    });
  };

  const pickPhoto = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) {
      Alert.alert('Izin foto diperlukan', 'Aktifkan izin galeri untuk upload foto sawah.');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.7,
    });
    if (result.canceled) return;

    const asset = result.assets?.[0];
    if (!asset?.uri) return;
    setCheckModal((prev) => ({ ...prev, photo: asset }));
  };

  const submitChecklist = async () => {
    const task = checkModal.task;
    if (!task?.task_id) return;

    setCheckModal((prev) => ({ ...prev, saving: true }));
    try {
      const fd = new FormData();
      fd.append('water_level', checkModal.water);
      fd.append('leaf_condition', checkModal.leaf);
      if (checkModal.notes?.trim()) fd.append('notes', checkModal.notes.trim());

      if (checkModal.photo?.uri) {
        fd.append('photo', {
          uri: checkModal.photo.uri,
          name: checkModal.photo.fileName || `monitoring-${Date.now()}.jpg`,
          type: checkModal.photo.mimeType || 'image/jpeg',
        });
      }

      await axios.post(`${API_BASE}/monitoring/tasks/${task.task_id}/submit`, fd, {
        headers: {
          ...authHdr,
          'Content-Type': 'multipart/form-data',
        },
      });

      setCheckModal(emptyChecklistState);
      await loadTasks({ silent: true, targetTab: tab });
      Alert.alert('Berhasil', 'Laporan monitoring tersimpan.');
    } catch (e) {
      Alert.alert('Gagal submit', getErr(e, 'Tidak bisa menyimpan laporan monitoring'));
    } finally {
      setCheckModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const openReminder = (task) => {
    setRemindModal({
      ...emptyRemindState,
      open: true,
      task,
      phone: task?.assignee_phone || task?.assigned_whatsapp_phone || '',
    });
  };

  const sendReminder = async () => {
    const taskId = remindModal.task?.task_id;
    if (!taskId) return;

    const phone = String(remindModal.phone || '').trim();
    if (!phone) {
      Alert.alert('Nomor WA kosong', 'Isi nomor WA tujuan dulu.');
      return;
    }

    setRemindModal((prev) => ({ ...prev, sending: true }));
    try {
      const defaultPhone = String(
        remindModal.task?.assignee_phone ||
        remindModal.task?.assigned_whatsapp_phone ||
        ''
      ).trim();
      const payload = { strict: true };
      if (phone && phone !== defaultPhone) {
        payload.phone = phone;
        payload.use_input_phone = true;
      }

      const r = await axios.post(
        `${API_BASE}/monitoring/tasks/${taskId}/remind`,
        payload,
        { headers: authHdr }
      );

      setRemindModal(emptyRemindState);
      await loadTasks({ silent: true, targetTab: tab });

      const sent = !!r.data?.sent;

      if (sent) {
        Alert.alert('Berhasil', 'Reminder monitoring terkirim via WhatsApp API.');
      } else {
        const reason = r.data?.details?.reason ? `\nAlasan: ${r.data.details.reason}` : '';
        Alert.alert(
          'Reminder belum terkirim',
          `Reminder belum terkirim via provider WhatsApp.${reason}`
        );
      }
    } catch (e) {
      const reason =
        e?.response?.data?.reason ||
        e?.response?.data?.details?.reason ||
        e?.response?.data?.details?.response?.reason ||
        null;
      const status = e?.response?.status;
      if (status === 502) {
        Alert.alert(
          'Provider WA gagal kirim',
          `Reminder tidak terkirim via provider WhatsApp.${reason ? `\nAlasan: ${reason}` : ''}`
        );
      } else {
        Alert.alert('Gagal kirim reminder', getErr(e, 'Tidak bisa membuat reminder WA'));
      }
    } finally {
      setRemindModal((prev) => ({ ...prev, sending: false }));
    }
  };

  const openAssign = (task) => {
    const assignedTaskPhoneList = parsePhoneListLocal(task?.assignee_phone || task?.assigned_whatsapp_phone || '');
    const pickedWorkerSet = new Set();
    const assignedToId = Number(task?.assigned_to);
    if (Number.isFinite(assignedToId)) pickedWorkerSet.add(assignedToId);
    assignedTaskPhoneList.forEach((phone) => {
      const mappedId = workerIdByPhone.get(phone);
      if (Number.isFinite(mappedId)) pickedWorkerSet.add(mappedId);
    });
    const selectedWorkerIds = Array.from(pickedWorkerSet);
    setAssignModal({
      ...emptyAssignState,
      open: true,
      task,
      selectedWorkerIds,
      phone: formatPhoneListLocal(assignedTaskPhoneList),
    });
  };

  const toggleAssignWorker = (worker) => {
    const workerId = Number(worker?.user_id);
    if (!Number.isFinite(workerId)) return;
    setAssignModal((prev) => {
      const prevSelectedIds = Array.isArray(prev.selectedWorkerIds) ? prev.selectedWorkerIds : [];
      const prevSelectedSet = new Set(
        prevSelectedIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id))
      );
      const prevWorkerPhones = new Set(
        Array.from(prevSelectedSet)
          .map((id) => normalizePhoneLocal(workerById.get(id)?.whatsapp_phone || ''))
          .filter(Boolean)
      );
      const manualPhones = parsePhoneListLocal(prev.phone).filter((p) => !prevWorkerPhones.has(p));

      if (prevSelectedSet.has(workerId)) prevSelectedSet.delete(workerId);
      else prevSelectedSet.add(workerId);

      const nextSelectedIds = Array.from(prevSelectedSet);
      const nextWorkerPhones = nextSelectedIds
        .map((id) => normalizePhoneLocal(workerById.get(id)?.whatsapp_phone || ''))
        .filter(Boolean);
      const mergedPhones = parsePhoneListLocal([
        ...nextWorkerPhones,
        ...manualPhones,
      ]);
      return {
        ...prev,
        selectedWorkerIds: nextSelectedIds,
        phone: formatPhoneListLocal(mergedPhones),
      };
    });
  };

  const clearAssignWorker = () => {
    setAssignModal((prev) => {
      const prevSelectedIds = Array.isArray(prev.selectedWorkerIds) ? prev.selectedWorkerIds : [];
      const selectedPhones = new Set(
        prevSelectedIds
          .map((id) => normalizePhoneLocal(workerById.get(Number(id))?.whatsapp_phone || ''))
          .filter(Boolean)
      );
      const manualPhones = parsePhoneListLocal(prev.phone).filter((p) => !selectedPhones.has(p));
      return { ...prev, selectedWorkerIds: [], phone: formatPhoneListLocal(manualPhones) };
    });
  };

  const saveAssignment = async () => {
    const task = assignModal.task;
    if (!task?.field_id || !task?.due_date) return;

    const selectedWorkerIds = Array.isArray(assignModal.selectedWorkerIds)
      ? assignModal.selectedWorkerIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
      : [];
    const selectedWorkerPhones = selectedWorkerIds
      .map((id) => normalizePhoneLocal(workerById.get(id)?.whatsapp_phone || ''))
      .filter(Boolean);
    const allPhones = parsePhoneListLocal([
      ...selectedWorkerPhones,
      ...parsePhoneListLocal(assignModal.phone),
    ]);
    const assignedTo = selectedWorkerIds.find((id) => Number(id) > 0) || null;
    if (!assignedTo && !allPhones.length) {
      Alert.alert('Data belum lengkap', 'Pilih minimal 1 tukang kerja atau isi nomor WA reminder.');
      return;
    }

    setAssignModal((prev) => ({ ...prev, saving: true }));
    try {
      await axios.post(
        `${API_BASE}/monitoring/tasks`,
        {
          field_id: Number(task.field_id),
          due_date: toYmdInput(task.due_date),
          cadence_days: Number(task.cadence_days || cadence || 5),
          assigned_to: Number.isFinite(assignedTo) ? assignedTo : null,
          assignee_phone: formatPhoneListLocal(allPhones) || null,
        },
        { headers: authHdr }
      );

      setAssignModal(emptyAssignState);
      await Promise.all([
        loadTasks({ silent: true, targetTab: tab }),
        loadWorkers({ silent: true }),
      ]);
      Alert.alert('Berhasil', 'Tukang kerja dan nomor WA reminder sudah diupdate.');
    } catch (e) {
      Alert.alert('Gagal simpan', getErr(e, 'Tidak bisa menyimpan assignment tukang kerja.'));
    } finally {
      setAssignModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const saveSchedule = async () => {
    if (!canRemind) return;
    const time = String(schedule.time || '').trim();
    if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)) {
      Alert.alert('Format jam salah', 'Gunakan format HH:mm, contoh 07:00');
      return;
    }

    setScheduleSaving(true);
    try {
      const payload = {
        enabled: !!schedule.enabled,
        time,
        timezone: schedule.timezone || 'Asia/Jakarta',
        cadence_days: Number(schedule.cadence_days || 5),
        dispatch_limit: Number(schedule.dispatch_limit || 100),
      };

      const r = await axios.put(`${API_BASE}/monitoring/schedule`, payload, { headers: authHdr });
      setSchedule((prev) => ({ ...prev, ...(r.data?.schedule || {}) }));
      Alert.alert('Tersimpan', 'Jadwal reminder harian otomatis sudah diupdate.');
    } catch (e) {
      Alert.alert('Gagal simpan jadwal', getErr(e, 'Tidak bisa menyimpan jadwal reminder'));
    } finally {
      setScheduleSaving(false);
    }
  };

  const dispatchNow = async () => {
    if (!canRemind) return;
    setDispatchingNow(true);
    try {
      const r = await axios.post(
        `${API_BASE}/monitoring/reminders/dispatch`,
        {
          limit: Number(schedule.dispatch_limit || 100),
          cadence_days: Number(schedule.cadence_days || 5),
        },
        { headers: authHdr }
      );
      await loadTasks({ silent: true, targetTab: tab });
      const sent = Number(r.data?.sent || 0);
      const failed = Number(r.data?.failed || 0);
      const total = Number(r.data?.total_targets || 0);
      Alert.alert('Dispatch selesai', `Target: ${total}\nTerkirim: ${sent}\nGagal: ${failed}`);
    } catch (e) {
      Alert.alert('Dispatch gagal', getErr(e, 'Tidak bisa kirim reminder otomatis sekarang'));
    } finally {
      setDispatchingNow(false);
    }
  };

  const toggleHistory = useCallback((fieldKey) => {
    setHistoryExpandedByField((prev) => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  }, []);

  const openPhotoViewer = useCallback((uri, title = '') => {
    const safeUri = String(uri || '').trim();
    if (!safeUri) return;
    setPhotoViewer({
      open: true,
      uri: safeUri,
      title: String(title || '').trim(),
    });
  }, []);

  const closePhotoViewer = useCallback(() => {
    setPhotoViewer({ open: false, uri: '', title: '' });
  }, []);

  if (!token) {
    return (
      <View style={[s.center, { backgroundColor: PADIPRO.bg }]}>
        <Text style={s.emptyTitle}>Monitoring Sawah</Text>
        <Text style={s.emptyText}>Token belum ada. Buka screen ini setelah login.</Text>
      </View>
    );
  }

  return (
    <View style={s.screen}>
      <ScrollView
        style={s.container}
        contentContainerStyle={s.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={PADIPRO.primary} />}
        showsVerticalScrollIndicator={false}
      >
        <View style={s.hero}>
          <EnterpriseHeader
            title="Monitoring Sawah"
            subtitle={`Cek tiap ${cadence} hari: air, daun, dan foto`}
            right={(
              <TouchableOpacity style={s.heroIconBtn} onPress={onRefresh} activeOpacity={0.85}>
                <PadiProIcon name="monitoring" size={16} color="#FFFFFF" strokeWidth={2} />
              </TouchableOpacity>
            )}
          />
        </View>

        <View style={s.summaryRow}>
          <View style={[s.summaryCard, s.summaryWarn]}>
            <Text style={s.summaryLabel}>Terlambat</Text>
            <Text style={s.summaryValue}>{counts.overdue ?? 0}</Text>
          </View>
          <View style={s.summaryCard}>
            <Text style={s.summaryLabel}>Menunggu</Text>
            <Text style={s.summaryValue}>{counts.pending ?? 0}</Text>
          </View>
          <View style={s.summaryCard}>
            <Text style={s.summaryLabel}>OK</Text>
            <Text style={s.summaryValue}>{counts.done ?? 0}</Text>
          </View>
        </View>

        {tab === 'open' && (
          <View style={s.progressSummaryRow}>
            <View style={[s.progressSummaryCard, s.progressSummaryWaiting]}>
              <Text style={s.progressSummaryLabel}>Menunggu Laporan</Text>
              <Text style={s.progressSummaryValue}>
                {Number(counts.waiting_report ?? progressCounts.waiting ?? 0)}
              </Text>
            </View>
            <View style={[s.progressSummaryCard, s.progressSummaryUnreminded]}>
              <Text style={s.progressSummaryLabel}>Belum Diingatkan</Text>
              <Text style={s.progressSummaryValue}>
                {Number(counts.unreminded_report ?? progressCounts.unreminded ?? 0)}
              </Text>
            </View>
          </View>
        )}

        <View style={s.segment}>
          <TouchableOpacity
            style={[s.segmentBtn, tab === 'open' && s.segmentBtnActive]}
            onPress={() => setTab('open')}
            activeOpacity={0.9}
          >
            <Text style={[s.segmentText, tab === 'open' && s.segmentTextActive]}>Perlu Dicek</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[s.segmentBtn, tab === 'done' && s.segmentBtnActive]}
            onPress={() => setTab('done')}
            activeOpacity={0.9}
          >
            <Text style={[s.segmentText, tab === 'done' && s.segmentTextActive]}>OK</Text>
          </TouchableOpacity>
        </View>

        {canRemind && (
          <View style={s.scheduleCard}>
            <View style={s.scheduleHeadRow}>
              <View style={{ flex: 1 }}>
                <Text style={s.scheduleTitle}>Jadwal Reminder Otomatis</Text>
                <Text style={s.scheduleSub}>Pekerja cukup buka link form, isi laporan, lalu submit</Text>
              </View>
              <TouchableOpacity
                style={[s.scheduleToggle, schedule.enabled && s.scheduleToggleOn]}
                onPress={() => setSchedule((p) => ({ ...p, enabled: !p.enabled }))}
                activeOpacity={0.85}
              >
                <Text style={[s.scheduleToggleText, schedule.enabled && s.scheduleToggleTextOn]}>
                  {schedule.enabled ? 'AKTIF' : 'OFF'}
                </Text>
              </TouchableOpacity>
            </View>

            <View style={s.scheduleRow}>
              <View style={s.scheduleInputWrap}>
                <Text style={s.scheduleLabel}>Jam kirim</Text>
                <TextInput
                  style={s.scheduleInput}
                  value={String(schedule.time || '')}
                  onChangeText={(v) => setSchedule((p) => ({ ...p, time: v }))}
                  placeholder="07:00"
                  placeholderTextColor={PADIPRO.muted}
                  autoCapitalize="none"
                />
              </View>
              <View style={s.scheduleInputWrap}>
                <Text style={s.scheduleLabel}>Interval hari</Text>
                <TextInput
                  style={s.scheduleInput}
                  value={String(schedule.cadence_days ?? 5)}
                  onChangeText={(v) => setSchedule((p) => ({ ...p, cadence_days: v.replace(/[^0-9]/g, '') }))}
                  keyboardType="number-pad"
                />
              </View>
              <View style={s.scheduleInputWrap}>
                <Text style={s.scheduleLabel}>Batas kirim</Text>
                <TextInput
                  style={s.scheduleInput}
                  value={String(schedule.dispatch_limit ?? 100)}
                  onChangeText={(v) => setSchedule((p) => ({ ...p, dispatch_limit: v.replace(/[^0-9]/g, '') }))}
                  keyboardType="number-pad"
                />
              </View>
            </View>

            <View style={s.scheduleBtnRow}>
              <TouchableOpacity
                style={[s.scheduleBtn, s.scheduleBtnPrimary, (scheduleSaving || scheduleLoading) && { opacity: 0.65 }]}
                onPress={saveSchedule}
                disabled={scheduleSaving || scheduleLoading}
              >
                <Text style={s.scheduleBtnPrimaryText}>
                  {scheduleSaving ? 'Menyimpan…' : 'Simpan Jadwal'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.scheduleBtn, s.scheduleBtnGhost, dispatchingNow && { opacity: 0.65 }]}
                onPress={dispatchNow}
                disabled={dispatchingNow}
              >
                <Text style={s.scheduleBtnGhostText}>
                  {dispatchingNow ? 'Kirim…' : 'Kirim Sekarang'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {!!errorText && (
          <View style={s.alert}>
            <Text style={s.alertText}>{errorText}</Text>
          </View>
        )}

        {loading ? (
          <View style={s.centerLoading}>
            <ActivityIndicator size="large" color={PADIPRO.primary} />
            <Text style={s.centerLoadingText}>Memuat tugas monitoring…</Text>
          </View>
        ) : items.length === 0 ? (
          <View style={s.emptyBox}>
            <Text style={s.emptyTitle}>Tidak ada tugas</Text>
            <Text style={s.emptyText}>Semua lahan sudah terpantau untuk periode ini.</Text>
          </View>
        ) : (
          items.map((item) => {
            const snapshot = getConditionSnapshot(item);
            const waterMeta = WATER_META[snapshot.water] || null;
            const leafMeta = LEAF_META[snapshot.leaf] || null;
            const progress = getReportProgress(item);
            const showChecklistAction = item.status !== 'done' && progress.key !== 'ok';
            const fieldKey = String(item.field_id || item.task_id || '');
            const rawHistory = Array.isArray(item.report_history) ? item.report_history : [];
            const history = rawHistory.map((entry, idx) => {
              const waterKey = String(entry?.water_level || '').trim().toLowerCase();
              const leafKey = String(entry?.leaf_condition || '').trim().toLowerCase();
              const waterLabel = WATER_META[waterKey]?.label || '-';
              const leafLabel = LEAF_META[leafKey]?.label || '-';
              const eventTime = entry?.checked_at || entry?.updated_at || entry?.due_date || null;
              const hasAttention = waterKey === 'kering' || ATTENTION_LEAF.has(leafKey);
              const photoUri = resolvePhotoUri(entry?.photo_url || entry?.photo_path || null);
              return {
                ...entry,
                _row_key: `${entry?.task_id || 'h'}-${idx}`,
                _water_label: waterLabel,
                _leaf_label: leafLabel,
                _event_time: eventTime,
                _attention: hasAttention,
                _photo_uri: photoUri,
              };
            });
            const expandedHistory = !!historyExpandedByField[fieldKey];
            const visibleHistory = expandedHistory ? history : history.slice(0, 4);
            const canExpandHistory = history.length > 4;
            const statusHint =
              item.status === 'done'
                ? 'Laporan terbaru sudah tersimpan'
                : snapshot.needsAttention
                  ? 'Perlu perhatian: cek ulang kondisi lahan'
                  : progress.hint;
            const reminderMetaText =
              progress.key === 'waiting'
                ? `Reminder terkirim: ${item.wa_last_notified_at ? fmtDateTime(item.wa_last_notified_at) : '-'}`
                : progress.key === 'ok'
                  ? (
                      item.status === 'done'
                        ? 'Laporan periode ini sudah masuk.'
                        : 'Laporan periode ini sudah masuk. Menunggu jadwal berikutnya.'
                    )
                  : 'Reminder belum dikirim.';
            const assignedName = String(item.assigned_to_name || '').trim();
            const assignedPhoneList = parsePhoneListLocal(item.assignee_phone || item.assigned_whatsapp_phone || '');
            const assignmentNameText = assignedName
              ? (
                  assignedPhoneList.length > 1
                    ? `${assignedName} +${assignedPhoneList.length - 1} nomor`
                    : assignedName
                )
              : 'Belum dipilih';
            const assignmentPhoneText = assignedPhoneList.length
              ? assignedPhoneList.join(', ')
              : 'Belum diisi';

            return (
              <View key={item.task_id} style={s.card}>
                <View style={s.cardHead}>
                  <View style={{ flex: 1 }}>
                    <Text style={s.cardTitle}>{item.field_name || `Lahan #${item.field_id}`}</Text>
                    <Text style={s.cardSub}>Jadwal cek: {fmtDate(item.due_date)}</Text>
                    <Text style={s.cardMeta}>{reminderMetaText}</Text>
                    <View style={s.assigneeMetaWrap}>
                      <Text style={s.assigneeMetaText}>Tukang: {assignmentNameText}</Text>
                      <Text style={s.assigneeMetaText}>WA Reminder: {assignmentPhoneText}</Text>
                    </View>
                  </View>
                  <View style={[s.badge, { borderColor: progress.border, backgroundColor: progress.bg }]}>
                    <Text style={[s.badgeText, { color: progress.tone }]}>{progress.label}</Text>
                  </View>
                </View>

                <View style={[s.resultBox, snapshot.needsAttention && s.resultBoxWarn]}>
                  <View style={s.conditionHead}>
                    <Text style={s.conditionHeadTitle}>Kondisi Terakhir</Text>
                    <Text style={s.conditionHeadSub}>{statusHint}</Text>
                  </View>

                  <View style={s.conditionChipRow}>
                    <View
                      style={[
                        s.conditionChip,
                        waterMeta
                          ? { backgroundColor: waterMeta.bg, borderColor: waterMeta.border }
                          : s.conditionChipNeutral,
                      ]}
                    >
                      <Text
                        style={[
                          s.conditionChipText,
                          waterMeta ? { color: waterMeta.tone } : s.conditionChipTextNeutral,
                        ]}
                      >
                        Air: {waterMeta?.label || '-'}
                      </Text>
                    </View>
                    <View
                      style={[
                        s.conditionChip,
                        leafMeta
                          ? { backgroundColor: leafMeta.bg, borderColor: leafMeta.border }
                          : s.conditionChipNeutral,
                      ]}
                    >
                      <Text
                        style={[
                          s.conditionChipText,
                          leafMeta ? { color: leafMeta.tone } : s.conditionChipTextNeutral,
                        ]}
                      >
                        Daun: {leafMeta?.label || '-'}
                      </Text>
                    </View>
                  </View>

                  {!!snapshot.photoUri ? (
                    <Image source={{ uri: snapshot.photoUri }} style={s.resultPhoto} resizeMode="cover" />
                  ) : (
                    <View style={s.resultPhotoEmpty}>
                      <Text style={s.resultPhotoEmptyText}>Belum ada foto kondisi terbaru</Text>
                    </View>
                  )}

                  <Text style={s.resultText}>Update: {fmtDate(snapshot.checkedAt)}</Text>
                  {!!snapshot.notes && (
                    <Text style={s.resultNote} numberOfLines={2}>
                      Catatan: {snapshot.notes}
                    </Text>
                  )}
                </View>

                <View style={s.timelineBox}>
                  <View style={s.timelineHead}>
                    <Text style={s.timelineTitle}>Riwayat Monitoring</Text>
                    <Text style={s.timelineCount}>{history.length} catatan</Text>
                  </View>

                  {visibleHistory.length === 0 ? (
                    <Text style={s.timelineEmpty}>Belum ada riwayat laporan untuk lahan ini.</Text>
                  ) : (
                    visibleHistory.map((entry, idx) => (
                      <View key={`${fieldKey}-${entry._row_key}`} style={s.timelineRow}>
                        <View style={s.timelineRail}>
                          <View style={[s.timelineDot, entry._attention && s.timelineDotWarn]} />
                          {idx < visibleHistory.length - 1 && <View style={s.timelineLine} />}
                        </View>
                        <View style={s.timelineContent}>
                          <Text style={s.timelineDate}>{fmtDateTime(entry._event_time)}</Text>
                          <Text style={s.timelineDetail}>
                            Air: {entry._water_label} | Daun: {entry._leaf_label}
                          </Text>
                          {!!entry?.notes && (
                            <Text style={s.timelineNote} numberOfLines={2}>
                              Catatan: {entry.notes}
                            </Text>
                          )}
                          {!!entry?._photo_uri && (
                            <TouchableOpacity
                              style={s.timelineThumbWrap}
                              activeOpacity={0.9}
                              onPress={() => openPhotoViewer(
                                entry._photo_uri,
                                `${item.field_name || `Lahan #${item.field_id}`} • ${fmtDateTime(entry._event_time)}`
                              )}
                            >
                              <Image source={{ uri: entry._photo_uri }} style={s.timelineThumb} resizeMode="cover" />
                              <Text style={s.timelineThumbHint}>Tap foto untuk perbesar</Text>
                            </TouchableOpacity>
                          )}
                          {!entry?._photo_uri && <Text style={s.timelinePhoto}>Foto: tidak ada</Text>}
                        </View>
                      </View>
                    ))
                  )}

                  {canExpandHistory && (
                    <TouchableOpacity
                      style={s.timelineToggleBtn}
                      onPress={() => toggleHistory(fieldKey)}
                      activeOpacity={0.9}
                    >
                      <Text style={s.timelineToggleText}>
                        {expandedHistory ? 'Ringkas riwayat' : 'Lihat semua riwayat'}
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>

                {item.status !== 'done' && snapshot.needsAttention && (
                  <View style={s.warningBar}>
                    <Text style={s.warningBarText}>
                      Indikasi masalah lahan. Prioritaskan cek air dan daun hari ini.
                    </Text>
                  </View>
                )}

                <View style={s.actionRow}>
                  {showChecklistAction ? (
                    <TouchableOpacity
                      style={[s.actionBtn, s.actionPrimary]}
                      onPress={() => openChecklist(item)}
                      activeOpacity={0.9}
                    >
                      <PadiProIcon name="catatan" size={14} color="#fff" />
                      <Text style={s.actionPrimaryText}>Isi Checklist</Text>
                    </TouchableOpacity>
                  ) : (
                    <View style={s.donePill}>
                      <PadiProIcon name="stage-ripening" size={14} color={PADIPRO.primary} />
                      <Text style={s.donePillText}>
                        {item.status === 'done' ? 'Laporan tersimpan' : 'Sudah dilaporkan'}
                      </Text>
                    </View>
                  )}

                  {canRemind && item.status !== 'done' && progress.key !== 'ok' && (
                    <TouchableOpacity
                      style={[s.actionBtn, s.actionGhost]}
                      onPress={() => openReminder(item)}
                      activeOpacity={0.9}
                    >
                      <PadiProIcon name="monitoring" size={14} color={PADIPRO.primary} />
                      <Text style={s.actionGhostText}>
                        {progress.key === 'waiting' ? 'Kirim Ulang' : 'Reminder WA'}
                      </Text>
                    </TouchableOpacity>
                  )}

                  {canRemind && (
                    <TouchableOpacity
                      style={[s.actionBtn, s.actionGhost]}
                      onPress={() => openAssign(item)}
                      activeOpacity={0.9}
                    >
                      <PadiProIcon name="operations" size={14} color={PADIPRO.primary} />
                      <Text style={s.actionGhostText}>Atur Tukang</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            );
          })
        )}
      </ScrollView>

      <Modal
        visible={photoViewer.open}
        transparent
        animationType="fade"
        onRequestClose={closePhotoViewer}
      >
        <View style={s.photoViewerBackdrop}>
          <View style={s.photoViewerCard}>
            <Text style={s.photoViewerTitle}>{photoViewer.title || 'Preview Foto Monitoring'}</Text>
            {!!photoViewer.uri ? (
              <Image source={{ uri: photoViewer.uri }} style={s.photoViewerImage} resizeMode="contain" />
            ) : (
              <View style={s.resultPhotoEmpty}>
                <Text style={s.resultPhotoEmptyText}>Foto tidak tersedia</Text>
              </View>
            )}
            <TouchableOpacity style={s.photoViewerClose} onPress={closePhotoViewer} activeOpacity={0.9}>
              <Text style={s.photoViewerCloseText}>Tutup</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={checkModal.open}
        transparent
        animationType="fade"
        onRequestClose={() => setCheckModal(emptyChecklistState)}
      >
        <View style={s.modalBackdrop}>
          <View style={s.modalCard}>
            <Text style={s.modalTitle}>Checklist Monitoring</Text>
            <Text style={s.modalSub}>{checkModal.task?.field_name || '-'}</Text>

            <Text style={s.modalLabel}>Kondisi air</Text>
            <View style={s.pillRow}>
              {WATER_OPTIONS.map((opt) => (
                <TouchableOpacity
                  key={opt.key}
                  style={[s.pillOpt, checkModal.water === opt.key && s.pillOptActive]}
                  onPress={() => setCheckModal((p) => ({ ...p, water: opt.key }))}
                >
                  <Text style={[s.pillText, checkModal.water === opt.key && s.pillTextActive]}>{opt.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={s.modalLabel}>Kondisi daun padi</Text>
            <View style={s.pillRow}>
              {LEAF_OPTIONS.map((opt) => (
                <TouchableOpacity
                  key={opt.key}
                  style={[s.pillOpt, checkModal.leaf === opt.key && s.pillOptActive]}
                  onPress={() => setCheckModal((p) => ({ ...p, leaf: opt.key }))}
                >
                  <Text style={[s.pillText, checkModal.leaf === opt.key && s.pillTextActive]}>{opt.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={s.modalLabel}>Catatan</Text>
            <TextInput
              value={checkModal.notes}
              onChangeText={(v) => setCheckModal((p) => ({ ...p, notes: v }))}
              placeholder="Contoh: ada daun kuning di sudut barat."
              placeholderTextColor={PADIPRO.muted}
              style={s.notesInput}
              multiline
            />

            <TouchableOpacity style={s.photoBtn} onPress={pickPhoto} activeOpacity={0.9}>
              <PadiProIcon name="foto" size={15} color={PADIPRO.primary} />
              <Text style={s.photoBtnText}>{checkModal.photo ? 'Ganti Foto' : 'Upload Foto Sawah'}</Text>
            </TouchableOpacity>

            {checkModal.photo?.uri ? (
              <Image source={{ uri: checkModal.photo.uri }} style={s.photoPreview} resizeMode="cover" />
            ) : checkModal.previousPhotoUri ? (
              <View>
                <Text style={s.previousPhotoLabel}>Foto laporan terakhir</Text>
                <Image source={{ uri: checkModal.previousPhotoUri }} style={s.photoPreview} resizeMode="cover" />
              </View>
            ) : null}

            <View style={s.modalActions}>
              <TouchableOpacity
                style={[s.modalBtn, s.modalBtnGhost]}
                onPress={() => setCheckModal(emptyChecklistState)}
                disabled={checkModal.saving}
              >
                <Text style={s.modalBtnGhostText}>Batal</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.modalBtn, s.modalBtnPrimary, checkModal.saving && { opacity: 0.7 }]}
                onPress={submitChecklist}
                disabled={checkModal.saving}
              >
                <Text style={s.modalBtnPrimaryText}>
                  {checkModal.saving ? 'Menyimpan…' : 'Submit Laporan'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={remindModal.open}
        transparent
        animationType="fade"
        onRequestClose={() => setRemindModal(emptyRemindState)}
      >
        <View style={s.modalBackdrop}>
          <View style={s.modalCard}>
            <Text style={s.modalTitle}>Reminder via WhatsApp API</Text>
            <Text style={s.modalSub}>{remindModal.task?.field_name || '-'}</Text>

            <Text style={s.modalLabel}>Nomor WA Tujuan (pisahkan koma jika lebih dari 1)</Text>
            <TextInput
              value={remindModal.phone}
              onChangeText={(v) => setRemindModal((p) => ({ ...p, phone: v }))}
              placeholder="contoh: 08123456789, 081298765432"
              placeholderTextColor={PADIPRO.muted}
              style={s.notesInput}
              keyboardType="phone-pad"
            />

            <View style={s.modalActions}>
              <TouchableOpacity
                style={[s.modalBtn, s.modalBtnGhost]}
                onPress={() => setRemindModal(emptyRemindState)}
                disabled={remindModal.sending}
              >
                <Text style={s.modalBtnGhostText}>Batal</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.modalBtn, s.modalBtnPrimary, remindModal.sending && { opacity: 0.7 }]}
                onPress={sendReminder}
                disabled={remindModal.sending}
              >
                <Text style={s.modalBtnPrimaryText}>
                  {remindModal.sending ? 'Mengirim…' : 'Kirim Reminder'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={assignModal.open}
        transparent
        animationType="fade"
        onRequestClose={() => setAssignModal(emptyAssignState)}
      >
        <View style={s.modalBackdrop}>
          <View style={s.modalCard}>
            <Text style={s.modalTitle}>Atur Tukang Kerja</Text>
            <Text style={s.modalSub}>{assignModal.task?.field_name || '-'}</Text>
            <Text style={s.modalHint}>
              Reminder WA dikirim ke nomor ini untuk tugas cek lahan ini.
            </Text>

            <Text style={s.modalLabel}>Pilih tukang kerja (bisa lebih dari 1)</Text>
            <View style={s.workerList}>
              <TouchableOpacity
                style={[
                  s.workerRow,
                  !(Array.isArray(assignModal.selectedWorkerIds) && assignModal.selectedWorkerIds.length) && s.workerRowActive,
                ]}
                onPress={clearAssignWorker}
                activeOpacity={0.9}
              >
                <View style={{ flex: 1 }}>
                  <Text
                    style={[
                      s.workerName,
                      !(Array.isArray(assignModal.selectedWorkerIds) && assignModal.selectedWorkerIds.length) && s.workerNameActive,
                    ]}
                  >
                    Manual (tanpa pilih user)
                  </Text>
                  <Text
                    style={[
                      s.workerPhone,
                      !(Array.isArray(assignModal.selectedWorkerIds) && assignModal.selectedWorkerIds.length) && s.workerPhoneActive,
                    ]}
                  >
                    Isi nomor WA sendiri di bawah
                  </Text>
                </View>
              </TouchableOpacity>

              {workersLoading ? (
                <View style={s.workerLoadingWrap}>
                  <ActivityIndicator color={PADIPRO.primary} />
                </View>
              ) : workers.length ? (
                workers.map((worker) => {
                  const workerId = Number(worker?.user_id);
                  const active =
                    Number.isFinite(workerId) &&
                    Array.isArray(assignModal.selectedWorkerIds) &&
                    assignModal.selectedWorkerIds.map((id) => Number(id)).includes(workerId);
                  const workerPhone = normalizePhoneLocal(worker?.whatsapp_phone || '');
                  return (
                    <TouchableOpacity
                      key={`worker-${workerId}`}
                      style={[s.workerRow, active && s.workerRowActive]}
                      onPress={() => toggleAssignWorker(worker)}
                      activeOpacity={0.9}
                    >
                      <View style={{ flex: 1 }}>
                        <Text style={[s.workerName, active && s.workerNameActive]}>
                          {worker?.name || `User #${workerId}`}
                        </Text>
                        <Text style={[s.workerPhone, active && s.workerPhoneActive]}>
                          {workerPhone || 'WA belum diisi'}
                        </Text>
                      </View>
                      {active && <Text style={s.workerTag}>Dipilih</Text>}
                    </TouchableOpacity>
                  );
                })
              ) : (
                <View style={s.workerLoadingWrap}>
                  <Text style={s.workerEmptyText}>Belum ada tukang kerja. Set dulu di menu Users.</Text>
                </View>
              )}
            </View>

            <Text style={s.modalLabel}>Nomor WA Reminder</Text>
            <TextInput
              value={assignModal.phone}
              onChangeText={(v) => setAssignModal((p) => ({ ...p, phone: v }))}
              placeholder="contoh: 081360854977, 081234567890"
              placeholderTextColor={PADIPRO.muted}
              style={s.notesInput}
              keyboardType="phone-pad"
            />

            {Array.isArray(assignModal.selectedWorkerIds) && assignModal.selectedWorkerIds.length > 0 && (
              <Text style={s.modalHint}>
                Tukang dipilih: {assignModal.selectedWorkerIds
                  .map((id) => workerById.get(Number(id))?.name || `User #${id}`)
                  .join(', ')}
              </Text>
            )}

            <View style={s.modalActions}>
              <TouchableOpacity
                style={[s.modalBtn, s.modalBtnGhost]}
                onPress={() => setAssignModal(emptyAssignState)}
                disabled={assignModal.saving}
              >
                <Text style={s.modalBtnGhostText}>Batal</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.modalBtn, s.modalBtnPrimary, assignModal.saving && { opacity: 0.7 }]}
                onPress={saveAssignment}
                disabled={assignModal.saving}
              >
                <Text style={s.modalBtnPrimaryText}>
                  {assignModal.saving ? 'Menyimpan…' : 'Simpan Assignment'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const s = StyleSheet.create({
  screen: { flex: 1, backgroundColor: PADIPRO.bg },
  container: { flex: 1, backgroundColor: PADIPRO.bg },
  content: { padding: 14, paddingBottom: 26, gap: 12 },

  hero: {
    borderRadius: 22,
    padding: 14,
    backgroundColor: PADIPRO.primary,
    borderWidth: 1,
    borderColor: '#1a7a4d',
    ...padiproShadow,
  },
  heroIconBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.35)',
  },

  summaryRow: { flexDirection: 'row', gap: 8 },
  summaryCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  summaryWarn: { borderColor: '#f5c39a', backgroundColor: '#fff7ec' },
  summaryLabel: { fontSize: 12, fontWeight: '700', color: PADIPRO.textStrong },
  summaryValue: { marginTop: 3, fontSize: 19, fontWeight: '900', color: PADIPRO.textStrong },
  progressSummaryRow: { flexDirection: 'row', gap: 8 },
  progressSummaryCard: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 11,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  progressSummaryWaiting: { borderColor: '#c4dcfb', backgroundColor: '#eef5ff' },
  progressSummaryUnreminded: { borderColor: '#f0d8a5', backgroundColor: '#fff9ee' },
  progressSummaryLabel: { fontSize: 11, fontWeight: '800', color: PADIPRO.textStrong },
  progressSummaryValue: { marginTop: 2, fontSize: 18, fontWeight: '900', color: PADIPRO.textStrong },

  segment: {
    flexDirection: 'row',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surfaceSunken,
    padding: 4,
  },
  segmentBtn: { flex: 1, alignItems: 'center', paddingVertical: 9, borderRadius: 9 },
  segmentBtnActive: { backgroundColor: '#fff' },
  segmentText: { fontWeight: '700', color: PADIPRO.textStrong },
  segmentTextActive: { color: PADIPRO.textStrong },

  scheduleCard: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 12,
    backgroundColor: '#ffffff',
    padding: 11,
  },
  scheduleHeadRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  scheduleTitle: { fontWeight: '900', color: PADIPRO.text, fontSize: 14 },
  scheduleSub: { marginTop: 2, color: PADIPRO.muted, fontSize: 12, fontWeight: '600' },
  scheduleToggle: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  scheduleToggleOn: { borderColor: PADIPRO.border, backgroundColor: PADIPRO.surfaceSunken },
  scheduleToggleText: { fontWeight: '800', color: PADIPRO.muted, fontSize: 11 },
  scheduleToggleTextOn: { color: PADIPRO.primary },
  scheduleRow: { marginTop: 10, flexDirection: 'row', gap: 8 },
  scheduleInputWrap: { flex: 1 },
  scheduleLabel: { fontWeight: '700', color: PADIPRO.textStrong, fontSize: 11, marginBottom: 5 },
  scheduleInput: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 9,
    paddingHorizontal: 10,
    paddingVertical: 8,
    color: PADIPRO.text,
    backgroundColor: '#fff',
  },
  scheduleBtnRow: { marginTop: 10, flexDirection: 'row', gap: 8 },
  scheduleBtn: {
    flex: 1,
    borderRadius: 9,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  scheduleBtnPrimary: { backgroundColor: PADIPRO.primary, borderColor: PADIPRO.primary },
  scheduleBtnPrimaryText: { color: '#fff', fontWeight: '800' },
  scheduleBtnGhost: { backgroundColor: '#fff', borderColor: PADIPRO.border },
  scheduleBtnGhostText: { color: PADIPRO.textStrong, fontWeight: '800' },

  alert: {
    borderWidth: 1,
    borderColor: '#fecaca',
    backgroundColor: '#fff1f2',
    borderRadius: 10,
    padding: 10,
  },
  alertText: { color: '#9f1239', fontWeight: '700' },

  card: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 14,
    padding: 12,
    backgroundColor: PADIPRO.surface,
  },
  cardHead: { flexDirection: 'row', gap: 10, alignItems: 'flex-start' },
  cardTitle: { fontWeight: '900', color: PADIPRO.text, fontSize: 15 },
  cardSub: { marginTop: 2, color: PADIPRO.muted, fontWeight: '700', fontSize: 12 },
  cardMeta: { marginTop: 4, color: PADIPRO.textStrong, fontWeight: '700', fontSize: 11 },
  assigneeMetaWrap: { marginTop: 5, gap: 1 },
  assigneeMetaText: { color: PADIPRO.textStrong, fontWeight: '700', fontSize: 11 },

  badge: {
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  badgeText: { fontWeight: '800', color: PADIPRO.textStrong, fontSize: 11 },

  resultBox: {
    marginTop: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surfaceSunken,
    padding: 10,
    gap: 7,
  },
  resultBoxWarn: { borderColor: '#f3d1c9', backgroundColor: '#fff8f6' },
  conditionHead: { gap: 2 },
  conditionHeadTitle: { color: PADIPRO.textStrong, fontWeight: '900', fontSize: 12 },
  conditionHeadSub: { color: PADIPRO.textStrong, fontWeight: '600', fontSize: 11 },
  conditionChipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  conditionChip: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#fff',
  },
  conditionChipNeutral: { borderColor: PADIPRO.border, backgroundColor: PADIPRO.surfaceSunken },
  conditionChipText: { fontWeight: '800', fontSize: 11 },
  conditionChipTextNeutral: { color: PADIPRO.textStrong },
  resultPhoto: {
    width: '100%',
    height: 130,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  resultPhotoEmpty: {
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surfaceSunken,
    height: 62,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  resultPhotoEmptyText: { color: PADIPRO.muted, fontWeight: '700', fontSize: 11, textAlign: 'center' },
  resultText: { color: PADIPRO.textStrong, fontWeight: '700', fontSize: 12 },
  resultNote: { color: PADIPRO.textStrong, fontWeight: '600', fontSize: 12 },
  timelineBox: {
    marginTop: 9,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 10,
    backgroundColor: PADIPRO.surfaceSunken,
    paddingVertical: 9,
    paddingHorizontal: 10,
    gap: 8,
  },
  timelineHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  timelineTitle: { color: PADIPRO.textStrong, fontWeight: '900', fontSize: 12 },
  timelineCount: { color: PADIPRO.muted, fontWeight: '700', fontSize: 11 },
  timelineEmpty: { color: PADIPRO.muted, fontWeight: '600', fontSize: 11 },
  timelineRow: { flexDirection: 'row', alignItems: 'stretch', gap: 8 },
  timelineRail: { width: 12, alignItems: 'center' },
  timelineDot: {
    width: 9,
    height: 9,
    borderRadius: 5,
    backgroundColor: '#5aa178',
    marginTop: 3,
  },
  timelineDotWarn: { backgroundColor: '#c66a4b' },
  timelineLine: {
    flex: 1,
    width: 2,
    marginTop: 4,
    backgroundColor: PADIPRO.border,
    borderRadius: 2,
  },
  timelineContent: { flex: 1, paddingBottom: 3 },
  timelineDate: { color: PADIPRO.textStrong, fontWeight: '800', fontSize: 11 },
  timelineDetail: { marginTop: 2, color: PADIPRO.textStrong, fontWeight: '700', fontSize: 11 },
  timelineNote: { marginTop: 2, color: PADIPRO.textStrong, fontWeight: '600', fontSize: 11 },
  timelinePhoto: { marginTop: 2, color: '#2f6b4b', fontWeight: '700', fontSize: 10 },
  timelineThumbWrap: {
    marginTop: 4,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 8,
    backgroundColor: '#ffffff',
    padding: 6,
    alignSelf: 'flex-start',
    width: 148,
  },
  timelineThumb: {
    width: '100%',
    height: 92,
    borderRadius: 6,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  timelineThumbHint: { marginTop: 4, color: PADIPRO.textStrong, fontWeight: '700', fontSize: 10, textAlign: 'center' },
  timelineToggleBtn: {
    marginTop: 2,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#ffffff',
  },
  timelineToggleText: { color: '#1f6f43', fontWeight: '800', fontSize: 11 },
  warningBar: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#f3d1c9',
    borderRadius: 9,
    backgroundColor: '#fff2ee',
    paddingVertical: 8,
    paddingHorizontal: 9,
  },
  warningBarText: { color: '#9a3e2b', fontWeight: '700', fontSize: 11 },

  actionRow: { marginTop: 11, flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderRadius: 10,
    paddingVertical: 9,
    paddingHorizontal: 11,
    borderWidth: 1,
  },
  actionPrimary: { backgroundColor: PADIPRO.primary, borderColor: PADIPRO.primary },
  actionPrimaryText: { color: '#fff', fontWeight: '800', fontSize: 12 },
  actionGhost: { backgroundColor: '#fff', borderColor: PADIPRO.border },
  actionGhostText: { color: PADIPRO.primary, fontWeight: '800', fontSize: 12 },
  donePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  donePillText: { color: PADIPRO.primary, fontWeight: '800', fontSize: 12 },

  centerLoading: { alignItems: 'center', justifyContent: 'center', paddingVertical: 34, gap: 8 },
  centerLoadingText: { color: PADIPRO.muted, fontWeight: '700' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 24 },
  emptyBox: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    borderRadius: 12,
    paddingVertical: 24,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: { color: PADIPRO.text, fontWeight: '900', fontSize: 16 },
  emptyText: { marginTop: 6, color: PADIPRO.muted, textAlign: 'center', fontWeight: '600' },

  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(9, 24, 15, 0.34)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
  },
  photoViewerBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(7, 18, 12, 0.78)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  photoViewerCard: {
    width: '100%',
    maxWidth: 820,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
    backgroundColor: PADIPRO.primaryDark,
    padding: 12,
  },
  photoViewerTitle: { color: PADIPRO.surfaceSunken, fontWeight: '800', fontSize: 13, marginBottom: 10 },
  photoViewerImage: {
    width: '100%',
    height: 420,
    borderRadius: 10,
    backgroundColor: PADIPRO.primaryDark,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
  },
  photoViewerClose: {
    alignSelf: 'flex-end',
    marginTop: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.24)',
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 7,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  photoViewerCloseText: { color: PADIPRO.surfaceSunken, fontWeight: '800', fontSize: 12 },
  modalCard: {
    width: '100%',
    maxWidth: 560,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: '#fff',
    padding: 14,
  },
  modalTitle: { fontSize: 16, fontWeight: '900', color: PADIPRO.text },
  modalSub: { marginTop: 2, color: PADIPRO.muted, fontWeight: '700' },
  modalHint: { marginTop: 4, color: PADIPRO.muted, fontSize: 11, fontWeight: '600' },
  modalLabel: { marginTop: 12, fontWeight: '800', color: PADIPRO.text },
  workerList: { marginTop: 8, gap: 8, maxHeight: 220 },
  workerRow: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 10,
    backgroundColor: '#fff',
    paddingHorizontal: 10,
    paddingVertical: 9,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  workerRowActive: { borderColor: '#98d9b2', backgroundColor: PADIPRO.surfaceSunken },
  workerName: { color: PADIPRO.textStrong, fontWeight: '800', fontSize: 12 },
  workerNameActive: { color: PADIPRO.primary },
  workerPhone: { marginTop: 2, color: PADIPRO.muted, fontWeight: '600', fontSize: 11 },
  workerPhoneActive: { color: '#2d6b49' },
  workerTag: {
    color: PADIPRO.primary,
    fontWeight: '800',
    fontSize: 10,
    borderWidth: 1,
    borderColor: '#98d9b2',
    backgroundColor: PADIPRO.surfaceSunken,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  workerLoadingWrap: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 10,
    backgroundColor: PADIPRO.surfaceSunken,
    paddingVertical: 12,
    paddingHorizontal: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  workerEmptyText: { color: PADIPRO.muted, fontWeight: '700', fontSize: 11, textAlign: 'center' },

  pillRow: { marginTop: 8, flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  pillOpt: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: '#fff',
  },
  pillOptActive: { borderColor: PADIPRO.primary, backgroundColor: PADIPRO.surfaceSunken },
  pillText: { color: PADIPRO.textStrong, fontWeight: '700' },
  pillTextActive: { color: PADIPRO.primary },

  notesInput: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 10,
    paddingHorizontal: 11,
    paddingVertical: 10,
    color: PADIPRO.text,
    minHeight: 44,
  },

  photoBtn: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 7,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  photoBtnText: { color: PADIPRO.primary, fontWeight: '800' },
  photoPreview: { marginTop: 10, width: '100%', height: 160, borderRadius: 10, backgroundColor: PADIPRO.surfaceSunken },
  previousPhotoLabel: { marginTop: 9, color: PADIPRO.textStrong, fontSize: 11, fontWeight: '700' },

  modalActions: { marginTop: 14, flexDirection: 'row', gap: 9 },
  modalBtn: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalBtnGhost: { borderColor: PADIPRO.border, backgroundColor: '#fff' },
  modalBtnGhostText: { color: PADIPRO.textStrong, fontWeight: '800' },
  modalBtnPrimary: { borderColor: PADIPRO.primary, backgroundColor: PADIPRO.primary },
  modalBtnPrimaryText: { color: '#fff', fontWeight: '800' },
});
